/* =========================================================================
 * THE NETWORK HALF of the pursuit field editor — the ONLY file here that
 * imports `lib/api.js`.
 *
 * Same split, and the same reason, as `fieldEditRun.js` beside `fieldEdit.js`:
 * `lib/api.js` reads `import.meta.env`, so anything importing it cannot be
 * driven under plain Node — and every RULE lives in `pursuitEdit.js` precisely
 * so `verify-spaces.mjs` can execute it rather than regex for it.
 *
 * Two calls, in this order, and the second is not optional:
 *   1. POST the one-field correction.
 *   2. Re-read the record DIRECTLY and compare.
 * ========================================================================= */

import { api } from '../../../lib/api.js'
import {
  readPursuitEditResult, confirmPursuitWrite, pursuitEditBefore, buildPursuitEditRequest,
} from './pursuitEdit.js'

/**
 * Load the field policy for a line.
 *
 * ⚠️ A FAILED POLICY READ MUST NOT DEGRADE OPEN. `readPursuitPolicy` gives
 * `ok: false` for anything it cannot parse, and `mayEditPursuitField` then
 * reports every field as not drawable — so the editor DISAPPEARS rather than
 * drawing inputs whose permissions nobody knows. The opposite default (assume
 * editable, let the server refuse) would draw a full set of live-looking
 * controls that 403 on every press.
 */
export async function loadPursuitFieldPolicy(line, { signal } = {}) {
  return api.pursuitFieldPolicy(line, { signal })
}

/**
 * The direct record read — `GET /api/pursuit/:line/:id`.
 *
 * ⚠️ NOT `/api/pursuits`, which is cursor-paged. The route's own response
 * carries a `note` saying so: confirming a write against a paged list is timing
 * noise dressed as proof. Returns the RAW unprojected property bag.
 */
export async function readPursuitRaw(line, id, { signal } = {}) {
  const res = await api.pursuitRecord(line, id, { signal })
  return res?.properties || null
}

/**
 * Apply one field correction and confirm it.
 *
 * Returns `{ verdict, confirmation }`. `verdict.kind === 'written'` is the only
 * success, and `confirmation.state` qualifies it — a `mismatch` means the route
 * said yes and the record disagrees, which the caller must render as NOT
 * corrected.
 */
export async function runPursuitEdit({ record, line, field, value, reason, gate, clearing }) {
  const id = String(record?.sourceId || '')
  const ln = String(line || record?.lineKey || '')
  const body = buildPursuitEditRequest({ line: ln, id, field, value, reason, gate, clearing })

  let res
  try {
    res = await api.pursuitFieldEdit(body, {
      entity: 'pursuit-field',
      entityId: `${ln}:${id}`,
      before: pursuitEditBefore(record, field),
    })
  } catch (err) {
    /* ⚠️ A REFUSAL ARRIVES AS A THROW, because `request()` throws ApiError on a
     * non-2xx — and both 403s (`data_repair_locked`, `field_forbidden`) and the
     * 400 (`no_updatable_fields`) are non-2xx. Measured, all three. So the
     * parsed body is read out of the error and handed to the SAME reader as a
     * success. Treating a throw as "network failure" would turn the server's
     * specific, actionable refusal — which names the grant and the exec who can
     * set it — into "something went wrong". */
    const body2 = err?.body
    if (body2 && typeof body2 === 'object') {
      return { verdict: readPursuitEditResult(body2, field), confirmation: null }
    }
    return {
      verdict: {
        kind: 'error',
        message: err?.message || 'The correction did not run.',
        hint: 'Nothing was written.',
      },
      confirmation: null,
    }
  }

  const verdict = readPursuitEditResult(res, field)
  if (verdict.kind !== 'written') return { verdict, confirmation: null }

  /* ⚠️ THE CONFIRMATION READ IS ITSELF ALLOWED TO FAIL WITHOUT UNDOING THE
   * VERDICT. The write is already done and cannot be rolled back, so a failed
   * re-read is reported as `unverified` — not as a failed correction. Calling a
   * completed write a failure would have somebody do it twice. */
  let afterRaw = null
  try {
    afterRaw = await readPursuitRaw(ln, id)
  } catch {
    afterRaw = null
  }

  return { verdict, confirmation: { afterRaw, expected: value } }
}

/** Composed separately so the component can pass the policy in — `confirmWrite`
 *  needs the spec to know a date from an enum, and the run half deliberately
 *  holds no policy of its own. */
export { confirmPursuitWrite }
